/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Button, Theme } from "@mui/material";
import { withMyTheme } from "../../theme/theme";
import { mobileCss } from "../../util/isMobile";

const CommonButtonStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    border-radius: 8px;
    text-transform: none;
    transition: all 0.3s ease;
`

const ButtonPrimaryStyle = withMyTheme((theme: Theme, additionalCss?: any) => css`
    background-color: ${theme.palette.primary.main};
    color: white;
    font-size: 1.1rem;
    padding: 12px 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    ${CommonButtonStyle}
    
    ${mobileCss(`
        font-size: 1.1rem;
        padding: 10px 20px;
    `)}
    
    &:hover {
        background-color: ${theme.palette.primary.dark};
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
        transform: translateY(-2px);
    }
    
    ${additionalCss ? additionalCss(theme) : ''}
`)

const ButtonSecondaryStyle = withMyTheme((theme: Theme, additionalCss?: any) => css`
    background-color: ${theme.palette.secondary.light};
    color: ${theme.palette.secondary.contrastText};
    font-size: 1.1rem;
    padding: 12px 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    ${CommonButtonStyle}
    
    ${mobileCss(`
        font-size: 1.1rem;
        padding: 10px 20px;
    `)}
    
    &:hover {
        background-color: ${theme.palette.secondary.main};
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
    }
    
    ${additionalCss ? additionalCss(theme) : ''}
`)

const ButtonWhiteStyle = withMyTheme((theme: Theme, additionalCss?: any) => css`
    background-color: white;
    color: ${theme.palette.primary.main};
    font-size: 1.1rem;
    padding: 12px 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    ${CommonButtonStyle}
    
    ${mobileCss(`
        font-size: 1.1rem;
        padding: 10px 20px;
    `)}
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.9);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
    }
    
    ${additionalCss ? additionalCss(theme) : ''}
`)

const StartIconStyle = withMyTheme((theme) => css`
    margin-right: 10px;
    ${mobileCss(`
        margin-right: 5px;
    `)}
`)

const EndIconStyle = withMyTheme((theme) => css`
    margin-left: 10px;
    ${mobileCss(`
        margin-left: 5px;
    `)}
`)

interface MyButtonProps {
    variant?: "primary" | "secondary" | "white";
    text: string,
    onClick?: () => void,
    additionalCss?: any,
    startIcon?: React.ReactNode,
    endIcon?: React.ReactNode,
    className?: string,
    disabled?: boolean,
    type?: "button" | "submit" | "reset"
}

export const MyButton = ({ 
    text, 
    variant = "primary", 
    onClick, 
    additionalCss, 
    startIcon, 
    endIcon, 
    className,
    disabled,
    type = "button"
}: MyButtonProps) => {
    // @ts-ignore
    let style;
    
    switch (variant) {
        case 'secondary':
            style = ButtonSecondaryStyle;
            break;
        case 'white':
            style = ButtonWhiteStyle;
            break;
        default:
            style = ButtonPrimaryStyle;
    }
    
    return <Button 
        // @ts-ignore
        css={(theme) => style(theme, additionalCss)}
        onClick={onClick} 
        startIcon={startIcon}
        endIcon={endIcon} 
        className={className}
        disabled={disabled}
        type={type}>
        {text}
    </Button>
}